import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { FeatureToggleViewModel } from '../model';

export enum FeatureToggleType {
  MATCHED_CANDIDATES_CSV_UNREDACTED_CONTACT_INFO = 'MATCHED_CANDIDATES_CSV_UNREDACTED_CONTACT_INFO',
  DEV_LOG_MANAGER = 'DEV_LOG_MANAGER',
  FILTER_INDEED_CANDIDATES_BASED_ON_USER_LICENSE = 'FILTER_INDEED_CANDIDATES_BASED_ON_USER_LICENSE',
  CENTRAL_INBOX = 'CENTRAL_INBOX',
  FIRST_FOLLOW_UP_IN_MINUTES = 'FIRST_FOLLOW_UP_IN_MINUTES',
  SECOND_FOLLOW_UP_IN_MINUTES = 'SECOND_FOLLOW_UP_IN_MINUTES',
  BEST_MATCH_SEARCH = 'BEST_MATCH_SEARCH',
  ENABLE_EMAIL_WITHOUT_NYLAS_CONNECTION = 'ENABLE_EMAIL_WITHOUT_NYLAS_CONNECTION',
  RJ_COMMUNICATION_HUB = 'RJ_COMMUNICATION_HUB',
  RJ_COMM_HUB_RESTRICT_DIRECT_CHANNELS = 'RJ_COMM_HUB_RESTRICT_DIRECT_CHANNELS',
  WA_COMMUNICATION_AGENT = 'WA_COMMUNICATION_AGENT',
  RJ_COMMUNICATION_AGENT_NO_ROBIN_VERIFIED = 'RJ_COMMUNICATION_AGENT_NO_ROBIN_VERIFIED',
  ALL_UNLOCK_AI_MESSAGE = 'ALL_UNLOCK_AI_MESSAGE',
  WHATSAPP_UNLOCK_AI_MESSAGE = 'WHATSAPP_UNLOCK_AI_MESSAGE',
  LINKEDIN_IN_MAIL_UNLOCK_AI_MESSAGE = 'LINKEDIN_IN_MAIL_UNLOCK_AI_MESSAGE',
  INDEED_UNLOCK_AI_MESSAGE = 'INDEED_UNLOCK_AI_MESSAGE',
  ROBIN_JR_CHAT = 'ROBIN_JR_CHAT',
  NYLAS_CALENDAR = 'NYLAS_CALENDAR',
  INDEED_POLLING_ENABLED = 'INDEED_POLLING_ENABLED',
  CUSTOMER_BRAND_IDENTITY = 'CUSTOMER_BRAND_IDENTITY',
  MAX_AVAILABILITY = 'MAX_AVAILABILITY',
}

type FeatureToggleStore = {
  featureToggles: FeatureToggleViewModel[];
  setFeatureToggles: (featureToggles: FeatureToggleViewModel[]) => void;
};

export const useFeatureToggleStore = create<FeatureToggleStore>()(
  persist(
    (set) => ({
      featureToggles: [],
      setFeatureToggles: (featureToggles) => set({ featureToggles }),
    }),
    {
      name: 'feature-toggle',
    },
  ),
);

type FeatureToggleSelector = FeatureToggleType | ((ft: typeof FeatureToggleType) => FeatureToggleType);

const getFeatureToggleSelectorValue = (featureToggle: FeatureToggleSelector): FeatureToggleType => {
  if (typeof featureToggle === 'function') {
    return featureToggle(FeatureToggleType);
  }

  return featureToggle;
};

export const getIsFeatureToggleEnabled = (featureToggleSelector: FeatureToggleSelector): boolean => {
  const name = getFeatureToggleSelectorValue(featureToggleSelector);

  const featureToggle = useFeatureToggleStore.getState().featureToggles.find((ft) => ft.name === name);

  return featureToggle?.enabled ?? false;
};

export const getFeatureToggleValue = (featureToggleSelector: FeatureToggleSelector): string | null => {
  const name = getFeatureToggleSelectorValue(featureToggleSelector);

  const featureToggle = useFeatureToggleStore.getState().featureToggles.find((ft) => ft.name === name);

  return featureToggle?.value ?? null;
};

export const useIsFeatureToggleEnabled = (featureToggleSelector: FeatureToggleSelector): boolean => {
  const featureToggles = useFeatureToggleStore((state) => state.featureToggles);
  const name = getFeatureToggleSelectorValue(featureToggleSelector);

  const featureToggle = featureToggles.find((ft) => ft.name === name);

  return featureToggle?.enabled ?? false;
};

export const useFeatureToggleValue = (featureToggleSelector: FeatureToggleSelector): string | null => {
  const featureToggles = useFeatureToggleStore((state) => state.featureToggles);
  const name = getFeatureToggleSelectorValue(featureToggleSelector);

  const featureToggle = featureToggles.find((ft) => ft.name === name);

  return featureToggle?.value ?? null;
};

export const getFeatureToggleActions = () => {
  const setFeatureToggles = useFeatureToggleStore.getState().setFeatureToggles;

  return { setFeatureToggles };
};

export const useFeatureToggleActions = () => {
  const setFeatureToggles = useFeatureToggleStore((state) => state.setFeatureToggles);

  return { setFeatureToggles };
};
